import { Button, Dialog, DialogBody, DialogContent, DialogFooter, Input, Link } from '@civilgrid/ui/components';
import { useState } from 'react';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { isValidMissiveToken, MISSIVE_TOKEN_PREFIX, MISSIVE_USER_GUIDE_URL, saveMissiveToken } from './missive';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a token is saved, so the parent can re-render with the connected state. */
  onConnected?: () => void;
}

/** One-time "Connect to Missive" setup: captures the user's PAT and saves it to localStorage. */
export default function ConnectMissiveModal({ open, onOpenChange, onConnected }: Props) {
  const [token, setToken] = useState('');

  const isValid = isValidMissiveToken(token);
  const showError = token.trim().length > 0 && !isValid;

  // Clear input + validation error on any close (Cancel, backdrop, Escape) so it isn't stale on reopen.
  const handleOpenChange = (next: boolean) => {
    if (!next) setToken('');
    onOpenChange(next);
  };

  const handleConnect = () => {
    if (!isValid) return;

    saveMissiveToken(token);
    capturePosthogEvent(POSTHOG_EVENTS.civilsearchMissiveConnected);
    onConnected?.();
    handleOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="connect-missive-modal" title="Connect your Missive account">
        <DialogBody className="flex flex-col gap-3">
          <p className="text-sm text-grey-80">
            This is a one-time connection. Paste your Missive personal access token below and Create Draft will just
            work from now on - drafts land in your own Missive, sent from you.
          </p>
          <Link
            href={MISSIVE_USER_GUIDE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="connect-missive-instructions-link"
          >
            How to find your Missive token →
          </Link>
          <Input
            type="password"
            aria-label="Missive personal access token"
            placeholder="Missive personal access token"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            data-testid="connect-missive-token-input"
          />
          {showError && (
            <p className="text-xs text-red-50" data-testid="connect-missive-token-error">
              That doesn't look right — a Missive token starts with "{MISSIVE_TOKEN_PREFIX}".
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => handleOpenChange(false)} data-testid="connect-missive-cancel">
            Cancel
          </Button>
          <Button variant="primary" disabled={!isValid} onClick={handleConnect} data-testid="connect-missive-save">
            Connect
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
